const mobileViewport = window.matchMedia('(max-width: 749px)');
const deferredMediaObserved = new WeakSet();
const autoplayVideoObserved = new WeakSet();
const viewportAnimationObserved = new WeakSet();
const videoStates = new WeakMap();
const pendingRoots = new Set();
let pendingFrame = 0;

document.documentElement.classList.add('emy-enhanced');

const runIdle = (callback) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 700 });
  } else {
    window.setTimeout(callback, 1);
  }
};

const queryWithin = (root, selector) => {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) elements.push(root);
  root.querySelectorAll?.(selector).forEach((element) => elements.push(element));

  return elements;
};

const isCriticalMedia = (element) => {
  const main = document.querySelector('#MainContent');
  const firstSection = main?.querySelector(':scope > .shopify-section');

  return Boolean(
    firstSection?.contains(element) ||
      element.closest('#header-group') ||
      element.getAttribute('loading') === 'eager' ||
      element.getAttribute('fetchpriority') === 'high'
  );
};

const getVideoState = (video) => {
  let state = videoStates.get(video);
  if (state) return state;

  state = {
    autoplay: video.autoplay || video.hasAttribute('autoplay'),
    src: video.getAttribute('src'),
    sources: Array.from(video.querySelectorAll('source')).map((source) => ({
      source,
      src: source.getAttribute('src'),
    })),
    suspensions: new Map(),
    unloaded: false,
  };

  videoStates.set(video, state);
  return state;
};

const unloadVideo = (video, state) => {
  if (state.unloaded) return;

  video.pause();
  video.autoplay = false;
  video.removeAttribute('autoplay');
  video.preload = 'none';

  if (video.hasAttribute('src')) video.removeAttribute('src');
  state.sources.forEach(({ source }) => source.removeAttribute('src'));

  state.unloaded = true;
  video.load();
};

const restoreVideoSources = (video, state) => {
  if (!state.unloaded) return;

  if (state.src) video.setAttribute('src', state.src);
  state.sources.forEach(({ source, src }) => {
    if (src) source.setAttribute('src', src);
  });

  if (state.autoplay) {
    video.autoplay = true;
    video.setAttribute('autoplay', '');
  }

  video.preload = isCriticalMedia(video) ? 'auto' : 'metadata';
  state.unloaded = false;
  video.load();
};

const syncVideoState = (video) => {
  const state = getVideoState(video);
  const isSuspended = state.suspensions.size > 0;
  const shouldUnload = Array.from(state.suspensions.values()).some(Boolean);

  if (isSuspended) {
    if (shouldUnload) {
      unloadVideo(video, state);
    } else {
      video.pause();
    }
    return;
  }

  restoreVideoSources(video, state);

  if (state.autoplay && !document.hidden) {
    video.play().catch(() => {});
  }
};

const setVideoSuspension = (video, reason, shouldSuspend, unload = false) => {
  const state = getVideoState(video);

  if (shouldSuspend) {
    state.suspensions.set(reason, unload);
  } else {
    state.suspensions.delete(reason);
  }

  syncVideoState(video);
};

const deferredMediaObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const media = entry.target;

            if (entry.isIntersecting && !document.hidden) {
              media.loadContent?.(false);
              media.playMedia?.();
              observeAutoplayVideos(media);
            } else {
              media.pauseMedia?.();
            }
          });
        },
        { rootMargin: '420px 0px', threshold: 0.01 }
      )
    : null;

const autoplayVideoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const critical = isCriticalMedia(video);

            setVideoSuspension(
              video,
              'viewport',
              !entry.isIntersecting || document.hidden,
              !critical
            );
          });
        },
        { rootMargin: '320px 0px', threshold: 0.01 }
      )
    : null;

const viewportAnimationObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-emy-in-view', entry.isIntersecting);
          });
        },
        { rootMargin: '180px 0px', threshold: 0.01 }
      )
    : null;

function optimizeImages(root = document) {
  queryWithin(root, 'img').forEach((image) => {
    image.decoding = 'async';

    if (!isCriticalMedia(image)) {
      if (image.getAttribute('loading') !== 'eager') image.loading = 'lazy';
      if (!image.hasAttribute('fetchpriority')) image.fetchPriority = 'low';
    }
  });
}

function syncResponsiveHeroVideos(root = document) {
  queryWithin(root, '.hero video').forEach((video) => {
    const wrapper = video.closest('.hero__media-wrapper');
    if (!wrapper) return;

    const desktopOnly =
      wrapper.classList.contains('hero__media-wrapper--desktop') &&
      !wrapper.classList.contains('hero__media-wrapper--mobile');
    const mobileOnly =
      wrapper.classList.contains('hero__media-wrapper--mobile') &&
      !wrapper.classList.contains('hero__media-wrapper--desktop');
    const inactive = mobileViewport.matches ? desktopOnly : mobileOnly;

    setVideoSuspension(video, 'responsive', inactive, inactive);
  });
}

function observeDeferredMedia(root = document) {
  queryWithin(root, 'deferred-media[autoplay]').forEach((media) => {
    if (deferredMediaObserved.has(media)) return;
    deferredMediaObserved.add(media);

    if (deferredMediaObserver) {
      deferredMediaObserver.observe(media);
    } else {
      media.loadContent?.(false);
      media.playMedia?.();
    }
  });
}

function observeAutoplayVideos(root = document) {
  queryWithin(root, 'video').forEach((video) => {
    const state = getVideoState(video);
    if (!state.autoplay || autoplayVideoObserved.has(video)) return;

    autoplayVideoObserved.add(video);
    video.preload = isCriticalMedia(video) ? 'auto' : 'metadata';

    if (autoplayVideoObserver) {
      autoplayVideoObserver.observe(video);
    } else if (!document.hidden) {
      video.play().catch(() => {});
    }
  });
}

function observeViewportAnimations(root = document) {
  queryWithin(root, '[data-emy-viewport-animation]').forEach((element) => {
    if (viewportAnimationObserved.has(element)) return;
    viewportAnimationObserved.add(element);

    if (viewportAnimationObserver) {
      viewportAnimationObserver.observe(element);
    } else {
      element.classList.add('is-emy-in-view');
    }
  });
}

function initializePerformanceOptimizations(root = document) {
  syncResponsiveHeroVideos(root);
  observeDeferredMedia(root);
  observeAutoplayVideos(root);
  observeViewportAnimations(root);
  runIdle(() => optimizeImages(root));
}

function queueInitialization(root) {
  if (!(root instanceof Element)) return;

  pendingRoots.add(root);
  if (pendingFrame) return;

  pendingFrame = window.requestAnimationFrame(() => {
    pendingFrame = 0;
    pendingRoots.forEach((pendingRoot) => initializePerformanceOptimizations(pendingRoot));
    pendingRoots.clear();
  });
}

function syncDocumentVisibility() {
  document.documentElement.classList.toggle('emy-page-hidden', document.hidden);

  document.querySelectorAll('video').forEach((video) => {
    const state = getVideoState(video);
    if (!state.autoplay) return;
    setVideoSuspension(video, 'document-hidden', document.hidden, false);
  });

  document.querySelectorAll('deferred-media[data-media-loaded]').forEach((media) => {
    if (document.hidden) {
      media.pauseMedia?.();
    } else if (media.getBoundingClientRect().top < window.innerHeight + 420) {
      media.playMedia?.();
    }
  });
}

function initializeWhenReady() {
  initializePerformanceOptimizations(document);

  if ('MutationObserver' in window && document.body) {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => queueInitialization(node));
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWhenReady, { once: true });
} else {
  initializeWhenReady();
}

mobileViewport.addEventListener?.('change', () => syncResponsiveHeroVideos(document));

document.addEventListener('shopify:section:load', (event) => {
  initializePerformanceOptimizations(event.target);
});

document.addEventListener('visibilitychange', syncDocumentVisibility);
syncDocumentVisibility();
